import type { CandidateBallotpediaBlock } from '@/types/ballotpedia';

// Candidate groups in the order they read on the ballot: the settled roster
// first, then write-ins, then the exceptions. Index is the sort key.
// withdrew/disqualified stay here for completeness but isCandidateHidden filters
// them out before sorting, so those slots never render.
export const CANDIDATE_STATUS_ORDER = [
  'onBallot',
  'writeIn',
  'withdrewOnBallot',
  'withdrew',
  'disqualified',
  'candidacyDeclared',
] as const;

export type CandidateStatusKind = (typeof CANDIDATE_STATUS_ORDER)[number];

export interface CandidateStatusDisplay {
  kind: CandidateStatusKind;
  /** Sort key — lower sorts earlier. */
  order: number;
  /** True for everything except a plain on-ballot candidate. */
  showBadge: boolean;
}

/**
 * Map a candidate's Ballotpedia block to a display group. Off-the-ballot
 * statuses (withdrew / disqualified) win over the write-in flag; a write-in
 * otherwise wins over "candidacy declared", since writing the name in is how
 * the voter casts that vote. Advanced/Won/Lost and missing status fall through
 * to on-ballot.
 */
export function getCandidateStatusDisplay(
  bp: CandidateBallotpediaBlock | undefined
): CandidateStatusDisplay {
  const status = (bp?.candidate_status ?? '').trim().toLowerCase();
  let kind: CandidateStatusKind;
  if (status === 'disqualified') kind = 'disqualified';
  else if (status === 'withdrew still on ballot') kind = 'withdrewOnBallot';
  else if (status === 'withdrew') kind = 'withdrew';
  else if (bp?.is_write_in) kind = 'writeIn';
  else if (status === 'candidacy declared') kind = 'candidacyDeclared';
  else kind = 'onBallot';

  return {
    kind,
    order: CANDIDATE_STATUS_ORDER.indexOf(kind),
    showBadge: kind !== 'onBallot',
  };
}

// Withdrawn and disqualified candidates are off the ballot, so they are hidden
// from the list entirely rather than shown with a status affordance.
const HIDDEN_STATUS_KINDS = new Set<CandidateStatusKind>(['withdrew', 'disqualified']);

export function isCandidateHidden(bp: CandidateBallotpediaBlock | undefined): boolean {
  return HIDDEN_STATUS_KINDS.has(getCandidateStatusDisplay(bp).kind);
}
